/**
 * Holding Detail Screen Component
 *
 * Main screen for viewing detailed information about a single mutual fund holding.
 * Features a tab-based layout with:
 * - Performance tab: Chart and returns comparison
 * - Rating History tab: Chart and list of rating changes
 * - Fund Info tab: AMC details, expense ratio, etc.
 * - Transactions tab: BUY/SELL transaction history
 */

import { useState, useMemo, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ArrowLeft, RefreshCw, AlertCircle } from "lucide-react";
import type { HoldingId, FundRating } from "@curie/shared";

// Import hook
import {
  useHoldingComplete,
  getHoldingErrorMessage,
  isHoldingNotFoundError,
  type HoldingDetail,
} from "@/hooks";

// Import all holding detail components
import {
  HoldingDetailHeader,
  HoldingDetailHeaderSkeleton,
  type HoldingDetailHeaderData,
} from "./HoldingDetailHeader";
import {
  PerformanceChart,
  PerformanceChartSkeleton,
  type PerformanceDataPoint,
} from "./performance-chart";
import {
  ReturnsComparisonTable,
  ReturnsComparisonTableSkeleton,
  type PeriodReturn,
} from "./ReturnsComparisonTable";
import {
  RatingHistoryChart,
  RatingHistoryChartSkeleton,
} from "./RatingHistoryChart";
import {
  RatingHistoryList,
  RatingHistoryListSkeleton,
} from "./RatingHistoryList";
import {
  AllocationBar,
  AllocationBarSkeleton,
  type AllocationSegment,
} from "./AllocationBar";
import {
  FundInfoTab,
  FundInfoTabSkeleton,
  extractFundInfoData,
} from "./FundInfoTab";
import {
  TransactionsTab,
  TransactionsTabSkeleton,
  type TransactionDisplayData,
} from "./TransactionsTab";
import {
  ScoreTrajectoryChart,
  ScoreTrajectoryChartSkeleton,
} from "./ScoreTrajectoryChart";

// ============================================================================
// Types
// ============================================================================

/**
 * Tab identifiers for the holding detail screen
 */
export type HoldingDetailTab = "performance" | "rating" | "info" | "transactions";

/**
 * Props for the HoldingDetailScreen component
 */
export interface HoldingDetailScreenProps {
  /** The holding ID to display */
  holdingId: HoldingId | string;
  /** Initial tab to show (used as fallback if no `tab` prop provided) */
  defaultTab?: HoldingDetailTab;
  /** Controlled tab value (for URL sync) */
  tab?: HoldingDetailTab;
  /** Callback when tab changes (for URL sync) */
  onTabChange?: (tab: HoldingDetailTab) => void;
  /** Callback when back button is pressed */
  onBack?: () => void;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Tab configuration
 */
const TABS: Array<{ id: HoldingDetailTab; label: string }> = [
  { id: "performance", label: "Performance" },
  { id: "rating", label: "Rating" },
  { id: "info", label: "Fund Info" },
  { id: "transactions", label: "Transactions" },
];

// ============================================================================
// Data Transformation Helpers
// ============================================================================

/**
 * Transform holding data to header format
 */
function transformToHeaderData(detail: HoldingDetail): HoldingDetailHeaderData {
  const { holding, fund } = detail;

  // Get the latest rating from rank history or use fund's rating
  const latestRating = fund.rankHistory?.[fund.rankHistory.length - 1];
  const rating: FundRating = latestRating?.rating ?? fund.rating ?? "on-track";

  return {
    fundName: fund.name,
    amcName: fund.amcName,
    // amcLogoUrl is optional in the UI component
    currentNav: fund.nav,
    navDate: fund.navDate,
    units: holding.units,
    currentValue: holding.currentValue,
    investedAmount: holding.investedAmount,
    totalReturns: holding.totalReturns,
    totalReturnsPercent: holding.totalReturnsPercent,
    rating,
  };
}

/**
 * Simple seeded random number generator for deterministic variation.
 * Uses a basic linear congruential generator (LCG) algorithm.
 * @param seed - Numeric seed value
 * @returns A function that returns deterministic pseudo-random numbers between 0 and 1
 */
function createSeededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    // LCG parameters (same as glibc)
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/**
 * Generate a numeric seed from a string (holding ID + date)
 */
function stringToSeed(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Generate deterministic performance data from fund nav history.
 * Uses a seeded random based on holding ID for consistent rendering.
 * In a real app, this would come from the API.
 */
function generatePerformanceData(detail: HoldingDetail): PerformanceDataPoint[] {
  // For demo purposes, generate synthetic data
  // In production, this would come from actual NAV history
  const baseValue = detail.holding.investedAmount;
  const currentValue = detail.holding.currentValue;
  const points: PerformanceDataPoint[] = [];

  // Create a deterministic seed based on holding ID and today's date (for daily consistency)
  const today = new Date();
  const dateStr = today.toISOString().split("T")[0] ?? "";
  const seed = stringToSeed(`${detail.holding.id}-${dateStr}`);
  const seededRandom = createSeededRandom(seed);

  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setMonth(date.getMonth() - i);

    // Linear interpolation with deterministic variation
    const progress = (6 - i) / 6;
    const variation = 1 + (seededRandom() - 0.5) * 0.02;
    const fundValue = baseValue + (currentValue - baseValue) * progress * variation;

    // Benchmark with slightly lower returns
    const benchmarkValue = baseValue + (currentValue - baseValue) * progress * 0.8 * variation;

    points.push({
      date: date.toISOString().split("T")[0] ?? "",
      fundValue: Math.round(fundValue),
      benchmarkValue: Math.round(benchmarkValue),
    });
  }

  return points;
}

/**
 * Get category multiplier based on fund rating.
 * In-form funds tend to beat their category more significantly,
 * while out-of-form funds may underperform relative to category.
 *
 * ESTIMATED DATA: Category returns are derived from fund performance
 * using these multipliers. Actual category data pending API support.
 */
function getCategoryMultiplierByRating(rating: FundRating): number {
  switch (rating) {
    case "in-form":
      // In-form funds significantly outperform category (category ~75% of fund returns)
      return 0.75;
    case "on-track":
      // On-track funds moderately outperform category (category ~85% of fund returns)
      return 0.85;
    case "off-track":
      // Off-track funds slightly outperform category (category ~92% of fund returns)
      return 0.92;
    case "out-of-form":
      // Out-of-form funds may underperform category (category ~105% of fund returns)
      return 1.05;
    default:
      return 0.85;
  }
}

/**
 * Transform fund returns to comparison table format.
 *
 * IMPORTANT: Category returns are ESTIMATED based on fund performance and rating.
 * This estimation is pending proper API support for actual category average data.
 * The multiplier varies by fund rating:
 * - "in-form" funds: Category estimated at 75% of fund returns (fund beats category by ~25%)
 * - "on-track" funds: Category estimated at 85% of fund returns (fund beats category by ~15%)
 * - "off-track" funds: Category estimated at 92% of fund returns (fund beats category by ~8%)
 * - "out-of-form" funds: Category estimated at 105% of fund returns (fund underperforms by ~5%)
 */
function transformToReturnsData(detail: HoldingDetail): PeriodReturn[] {
  const { fund } = detail;

  // Get the current rating from rank history or fund rating
  const latestRating = fund.rankHistory?.[fund.rankHistory.length - 1];
  const rating: FundRating = latestRating?.rating ?? fund.rating ?? "on-track";

  // Calculate category multiplier based on fund's rating
  // This creates more realistic estimates where in-form funds beat category more
  const categoryMultiplier = getCategoryMultiplierByRating(rating);

  return [
    {
      period: "1Y",
      fundReturn: fund.returns1Y,
      categoryReturn: fund.returns1Y * categoryMultiplier,
      isCategoryEstimated: true, // Pending API support for actual category data
    },
    {
      period: "3Y",
      fundReturn: fund.returns3Y,
      categoryReturn: fund.returns3Y * categoryMultiplier,
      isCategoryEstimated: true, // Pending API support for actual category data
    },
    {
      period: "5Y",
      fundReturn: fund.returns5Y,
      categoryReturn: fund.returns5Y * categoryMultiplier,
      isCategoryEstimated: true, // Pending API support for actual category data
    },
  ];
}

// Note: RatingHistoryChart and ScoreTrajectoryChart take rankHistory directly
// No transformation needed as the components handle RankHistoryEntry[]

/**
 * Transform fund allocation data based on category
 */
function transformToAllocation(detail: HoldingDetail): AllocationSegment[] {
  const { fund } = detail;
  const category = fund.category;

  // Derive allocation from fund category
  if (category.startsWith("equity-")) {
    return [
      { assetClass: "equity", percentage: 95 },
      { assetClass: "debt", percentage: 3 },
      { assetClass: "other", percentage: 2 },
    ];
  }

  if (category.startsWith("debt-")) {
    return [
      { assetClass: "debt", percentage: 90 },
      { assetClass: "equity", percentage: 5 },
      { assetClass: "other", percentage: 5 },
    ];
  }

  if (category.startsWith("hybrid-")) {
    if (category === "hybrid-aggressive") {
      return [
        { assetClass: "equity", percentage: 75 },
        { assetClass: "debt", percentage: 20 },
        { assetClass: "other", percentage: 5 },
      ];
    }
    if (category === "hybrid-conservative") {
      return [
        { assetClass: "debt", percentage: 75 },
        { assetClass: "equity", percentage: 20 },
        { assetClass: "other", percentage: 5 },
      ];
    }
    // Balanced hybrid
    return [
      { assetClass: "equity", percentage: 55 },
      { assetClass: "debt", percentage: 40 },
      { assetClass: "other", percentage: 5 },
    ];
  }

  // Default allocation
  return [
    { assetClass: "equity", percentage: 60 },
    { assetClass: "debt", percentage: 30 },
    { assetClass: "other", percentage: 10 },
  ];
}

/**
 * Transform transactions to display format
 */
function transformToTransactions(detail: HoldingDetail): TransactionDisplayData[] {
  const { transactions } = detail;

  if (!transactions) return [];

  return transactions.map((tx) => ({
    id: tx.id,
    date: tx.date,
    type: tx.type,
    units: tx.units,
    nav: tx.nav,
    amount: tx.amount,
  }));
}

// ============================================================================
// Subcomponents
// ============================================================================

/**
 * Screen header with back button and title
 */
function ScreenHeader({
  fundName,
  onBack,
  onRefresh,
  isRefreshing,
}: {
  fundName?: string;
  onBack?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-9 w-9"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        {fundName && (
          <h1 className="text-lg font-semibold truncate max-w-[200px] sm:max-w-none">
            {fundName}
          </h1>
        )}
      </div>
      {onRefresh && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="h-9 w-9"
          aria-label="Refresh data"
        >
          <RefreshCw
            className={cn("h-4 w-4", isRefreshing && "animate-spin")}
          />
        </Button>
      )}
    </div>
  );
}

/**
 * Error state display
 */
function ErrorState({
  error,
  onRetry,
  onBack,
}: {
  error: unknown;
  onRetry: () => void;
  onBack?: () => void;
}) {
  const isNotFound = isHoldingNotFoundError(error);
  const message = getHoldingErrorMessage(error);

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
      </div>
      <h2 className="text-lg font-semibold mb-2">
        {isNotFound ? "Holding Not Found" : "Error Loading Holding"}
      </h2>
      <p className="text-muted-foreground text-center mb-6 max-w-md">
        {message}
      </p>
      <div className="flex gap-3">
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            Go Back
          </Button>
        )}
        {!isNotFound && (
          <Button onClick={onRetry}>
            Try Again
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Performance tab content
 */
function PerformanceTabContent({
  detail,
  isLoading,
}: {
  detail?: HoldingDetail;
  isLoading: boolean;
}) {
  const performanceData = useMemo(
    () => (detail ? generatePerformanceData(detail) : []),
    [detail]
  );

  const returnsData = useMemo(
    () => (detail ? transformToReturnsData(detail) : []),
    [detail]
  );

  const allocationData = useMemo(
    () => (detail ? transformToAllocation(detail) : []),
    [detail]
  );

  if (isLoading || !detail) {
    return (
      <div className="space-y-4">
        <PerformanceChartSkeleton />
        <ReturnsComparisonTableSkeleton />
        <AllocationBarSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PerformanceChart
        data={performanceData}
        investedAmount={detail.holding.investedAmount}
        currentValue={detail.holding.currentValue}
        benchmarkName={detail.fund.benchmarkIndex}
        fundName={detail.fund.name}
      />
      <ReturnsComparisonTable returns={returnsData} fundName={detail.fund.name} />
      <AllocationBar allocations={allocationData} />
    </div>
  );
}

/**
 * Rating History tab content
 */
function RatingTabContent({
  detail,
  isLoading,
}: {
  detail?: HoldingDetail;
  isLoading: boolean;
}) {
  // Get current rating from fund or latest history entry
  const currentRating = useMemo(() => {
    if (!detail) return undefined;
    const history = detail.fund.rankHistory ?? [];
    const latest = history[history.length - 1];
    return latest?.rating ?? detail.fund.rating;
  }, [detail]);

  if (isLoading || !detail) {
    return (
      <div className="space-y-4">
        <RatingHistoryChartSkeleton />
        <ScoreTrajectoryChartSkeleton />
        <RatingHistoryListSkeleton />
      </div>
    );
  }

  const rankHistory = detail.fund.rankHistory ?? [];

  return (
    <div className="space-y-4">
      <RatingHistoryChart
        rankHistory={rankHistory}
        currentRating={currentRating}
      />
      <ScoreTrajectoryChart
        rankHistory={rankHistory}
        rating={currentRating ?? "on-track"}
        categoryBenchmark={50}
      />
      <RatingHistoryList history={rankHistory} />
    </div>
  );
}

/**
 * Fund Info tab content
 */
function FundInfoTabContent({
  detail,
  isLoading,
}: {
  detail?: HoldingDetail;
  isLoading: boolean;
}) {
  if (isLoading || !detail) {
    return <FundInfoTabSkeleton />;
  }

  // extractFundInfoData takes Fund and optional fundManager string
  // Fund manager is not in the base Fund type, so we pass undefined
  const fundInfo = extractFundInfoData(detail.fund);

  return <FundInfoTab data={fundInfo} />;
}

/**
 * Transactions tab content
 */
function TransactionsTabContent({
  detail,
  isLoading,
}: {
  detail?: HoldingDetail;
  isLoading: boolean;
}) {
  const transactions = useMemo(
    () => (detail ? transformToTransactions(detail) : []),
    [detail]
  );

  if (isLoading || !detail) {
    return <TransactionsTabSkeleton />;
  }

  return (
    <TransactionsTab
      holdingId={detail.holding.id}
      transactions={transactions}
    />
  );
}

// ============================================================================
// Loading Skeleton
// ============================================================================

/**
 * Full screen skeleton loader
 */
export function HoldingDetailScreenSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col min-h-screen", className)}>
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-4 px-4 pt-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-5 w-40" />
        </div>
        <Skeleton className="h-9 w-9 rounded-md" />
      </div>

      {/* Holding header skeleton */}
      <div className="px-4">
        <HoldingDetailHeaderSkeleton />
      </div>

      {/* Tabs skeleton */}
      <div className="mt-4 px-4">
        <Skeleton className="h-10 w-full rounded-lg mb-4" />
        <div className="space-y-4">
          <Skeleton className="h-64 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Holding Detail Screen
 *
 * Displays comprehensive information about a single mutual fund holding,
 * organized into tabs for easy navigation.
 *
 * @example
 * ```tsx
 * function HoldingPage({ params }: { params: { id: string } }) {
 *   const router = useRouter();
 *
 *   return (
 *     <HoldingDetailScreen
 *       holdingId={params.id}
 *       onBack={() => router.back()}
 *       defaultTab="performance"
 *     />
 *   );
 * }
 * ```
 */
export function HoldingDetailScreen({
  holdingId,
  defaultTab = "performance",
  tab,
  onTabChange,
  onBack,
  className,
}: HoldingDetailScreenProps) {
  // Use controlled tab if provided, otherwise use internal state
  const [internalTab, setInternalTab] = useState<HoldingDetailTab>(tab ?? defaultTab);

  // Sync internal state when controlled tab prop changes
  useEffect(() => {
    if (tab !== undefined) {
      setInternalTab(tab);
    }
  }, [tab]);

  // The active tab is either controlled externally or managed internally
  const activeTab = tab ?? internalTab;

  // Handle tab change - update internal state and notify parent
  const handleTabChange = (newTab: HoldingDetailTab) => {
    setInternalTab(newTab);
    onTabChange?.(newTab);
  };

  // Fetch holding data with all related information
  const {
    data: detail,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useHoldingComplete(holdingId);

  // Transform data for header
  const headerData = useMemo(
    () => (detail ? transformToHeaderData(detail) : undefined),
    [detail]
  );

  // Handle error state
  if (isError) {
    return (
      <div className={cn("flex flex-col min-h-screen", className)}>
        <ScreenHeader onBack={onBack} />
        <ErrorState error={error} onRetry={refetch} onBack={onBack} />
      </div>
    );
  }

  // Handle loading state
  if (isLoading) {
    return <HoldingDetailScreenSkeleton className={className} />;
  }

  return (
    <div className={cn("flex flex-col min-h-screen bg-background", className)}>
      {/* Screen Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b px-4 py-2">
        <ScreenHeader
          fundName={detail?.fund.name}
          onBack={onBack}
          onRefresh={refetch}
          isRefreshing={isRefetching}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 px-4 py-4 space-y-4">
        {/* Holding Detail Header */}
        {headerData ? (
          <HoldingDetailHeader data={headerData} />
        ) : (
          <HoldingDetailHeaderSkeleton />
        )}

        {/* Tabbed Content */}
        <Tabs
          value={activeTab}
          onValueChange={(value) => handleTabChange(value as HoldingDetailTab)}
          className="w-full"
        >
          <TabsList className="w-full grid grid-cols-4">
            {TABS.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="text-xs sm:text-sm"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="performance" className="mt-4">
            <PerformanceTabContent detail={detail} isLoading={isLoading} />
          </TabsContent>

          <TabsContent value="rating" className="mt-4">
            <RatingTabContent detail={detail} isLoading={isLoading} />
          </TabsContent>

          <TabsContent value="info" className="mt-4">
            <FundInfoTabContent detail={detail} isLoading={isLoading} />
          </TabsContent>

          <TabsContent value="transactions" className="mt-4">
            <TransactionsTabContent detail={detail} isLoading={isLoading} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default HoldingDetailScreen;
